/* eslint-disable css-modules/no-unused-class, no-extra-boolean-cast, no-compare-neg-zero */
import { FC, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { coreService } from 'network-utils';
import { LearningPathNodeProps, LearningPathNodeType } from '../learningPathInterfaces';
import Inventory from '../Inventory/inventory';
import styles from './learningPathMap.module.scss';

const LearningPathMap: FC<LearningPathMapProps> = (props: LearningPathMapProps) => {
    const { nodes, levelToNodeIdsMap, scrollPercent, level, badgeName } = props;
    const [nodeHovered, setNodeHovered] = useState<LearningPathNodeProps>();
    const mapIconRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [highlightX, setHighlightX] = useState<number>(0);
    const [highlightY, setHighlightY] = useState<number>(0);

    const renderIcon = (node: LearningPathNodeProps) => {
        const isSquare = node.type === LearningPathNodeType.MIXED;
        const isStarted = node.status === coreService.ProgressStatus.STARTED;

        return (
            <div
                className={classNames({
                    [styles['circle-border']]: isStarted && !isSquare,
                    [styles.circle]: !isStarted && !isSquare,
                    [styles['square-border']]: isStarted && isSquare,
                    [styles.square]: !isStarted && isSquare
                })}
            ></div>
        );
    };

    const handleMouseEnter = (node: LearningPathNodeProps) => {
        setNodeHovered(nodes.find(tmpNode => tmpNode.id === node.id));
    };

    const handleMouseLeave = () => {
        setNodeHovered(undefined);
    };

    useEffect(() => {
        const width = wrapperRef.current?.clientWidth;
        if (!width) return;

        setHighlightX(width / 2);
    }, [wrapperRef.current?.clientWidth]);

    useEffect(() => {
        const height = wrapperRef.current?.clientHeight;
        if (!height) return;
        const scrollFraction = (100 - scrollPercent) / 100;
        setHighlightY((height - 120) * scrollFraction + 60);
    }, [scrollPercent]);

    const levels = Object.keys(levelToNodeIdsMap)
        .map(level => parseInt(level, 10))
        .sort()
        .reverse();

    return (
        <div>
            <div ref={wrapperRef} className={styles['map-wrapper']}>
                <img
                    src={
                        badgeName
                            ? `${process.env.PUBLIC_URL}/images/learningPathBadges/${badgeName}.svg`
                            : `${process.env.PUBLIC_URL}/images/smallLearningPathCertificate.png`
                    }
                    style={{ height: '80px', width: '80px' }}
                    alt=""
                    loading="lazy"
                />
                {levels.map(level => {
                    const nodes = levelToNodeIdsMap[level];

                    return (
                        <div ref={mapIconRef} key={level} style={{ display: 'flex', flexDirection: 'row' }}>
                            {nodes.map(node => {
                                return (
                                    <div
                                        key={node.id}
                                        style={{ marginLeft: '0.4rem', marginRight: '0.4rem', position: 'relative' }}
                                        onMouseEnter={() => handleMouseEnter(node)}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        {renderIcon(node)}
                                        {nodeHovered && nodeHovered.id === node.id && (
                                            <div
                                                style={{
                                                    right: '2rem',
                                                    position: 'absolute',
                                                    zIndex: 999,
                                                    pointerEvents: 'none'
                                                }}
                                            >
                                                <Inventory
                                                    name={nodeHovered.name}
                                                    inventoryItems={nodeHovered.itemTypesCount}
                                                    maxScore={nodeHovered.potentialScore}
                                                    score={nodeHovered.score}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
                <div
                    className={styles.highlight}
                    style={
                        level === 0 && scrollPercent === -0
                            ? { top: undefined, bottom: '40px', right: highlightX }
                            : { top: highlightY, right: highlightX }
                    }
                ></div>
            </div>
        </div>
    );
};

export interface LearningPathMapProps {
    nodes: LearningPathNodeProps[];
    levelToNodeIdsMap: { [key: number]: LearningPathNodeProps[] };
    scrollPercent: number;
    level: number;
    badgeName?: string;
}

export default LearningPathMap;
